export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function request(path, { method = "GET", timeout = 20000, tries = 2, body, headers = {} } = {}) {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeout);
    try {
      const res = await fetch(`${API}${path}`, {
        method, signal: ctrl.signal,
        headers: { ...(body ? { "Content-Type": "application/json" } : {}), ...headers },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      clearTimeout(timer);
      const text = await res.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        // e.g. the web server answered /api with the app's index.html instead of proxying to the backend
        throw new Error(`${method} ${path}: API did not return JSON (status ${res.status}) — check that /api reaches the backend`);
      }
      if (!res.ok) throw new Error(data.detail || `${method} ${path} failed`);
      return data;
    } catch (e) {
      clearTimeout(timer);
      lastErr = e;
      if (i < tries - 1) await new Promise((r) => setTimeout(r, 700));
    }
  }
  throw lastErr;
}

const obx = () => ({ "X-Outbox-Password": sessionStorage.getItem("vigilo_outbox_pw") || "" });

export const api = {
  telemetry: () => request("/telemetry"),
  observations: () => request("/observations"),
  events: () => request("/events"),
  hypotheses: () => request("/hypotheses"),
  observation: (uid) => request(`/observations/${uid}`),
  hypothesize: (uid) => request(`/observations/${uid}/hypothesize`, { method: "POST", timeout: 150000, tries: 1 }),
  shakemaps: () => request("/shakemaps", { timeout: 60000 }),
  rainfall: () => request("/rainfall"),
  cyclones: () => request("/cyclones", { timeout: 60000 }),
  confirmed: () => request("/confirmed"),
  predictionSchedule: () => request("/predictions/schedule"),
  falsePositive: () => request("/metrics/false-positive"),
  alertPolicy: () => request("/alert-policy"),
  confirmedSkipped: () => request("/confirmed/skipped"),
  confidenceHistogram: () => request("/metrics/confidence-histogram"),
  nearMisses: () => request("/predictions/near-misses"),
  leaderboard: () => request("/leaderboard"),
  nationalCoverage: () => request("/national/coverage", { timeout: 30000 }),
  scoringLedger: () => request("/scoring/ledger", { timeout: 60000 }),
  widgetLatest: () => request("/widget/latest"),
  outbox: () => request("/outbox"),
  outboxCheck: (pw) => request("/outbox/check", { headers: { "X-Outbox-Password": pw }, tries: 1 }),
  editOutbox: (uid, body) => request(`/outbox/${uid}`, { method: "PATCH", body, tries: 1, headers: obx() }),
  approveOutbox: (uid) => request(`/outbox/${uid}/approve`, { method: "POST", timeout: 120000, tries: 1, headers: obx() }),
  notifyOutbox: (uid) => request(`/outbox/${uid}/notify`, { method: "POST", timeout: 30000, tries: 1, headers: obx() }),
  rejectOutbox: (uid) => request(`/outbox/${uid}/reject`, { method: "POST", tries: 1, headers: obx() }),
  countries: () => request("/countries"),
  adminCheck: (key) => request("/manual-confirm/check", { headers: { "X-Admin-Key": key }, tries: 1 }),
  manualCandidates: (key, q) => request(`/manual-confirm/candidates?q=${encodeURIComponent(q || "")}`, { headers: { "X-Admin-Key": key }, tries: 1 }),
  manualList: (key) => request("/manual-confirm", { headers: { "X-Admin-Key": key }, tries: 1 }),
  manualConfirm: (key, uid, body) => request(`/predictions/${uid}/manual-confirm`, { method: "POST", body, headers: { "X-Admin-Key": key }, tries: 1 }),
  manualRevert: (key, uid) => request(`/predictions/${uid}/manual-confirm/revert`, { method: "POST", headers: { "X-Admin-Key": key }, tries: 1 }),
  events: () => request("/events"),
  misses: () => request("/misses"),
  landslideDams: () => request("/landslide-dams"),
  feltQuakes: () => request("/quakes/felt", { timeout: 90000 }),
  damRecord: () => request("/dam-record"),
  rerunDamRecord: () => request("/dam-record/rerun", { method: "POST" }),
  ingest: () => request("/ingest", { method: "POST", timeout: 90000, tries: 1 }),
  sweep: () => request("/triage/sweep", { method: "POST", timeout: 120000, tries: 1 }),
  alerts: () => request("/alerts"),
  ackAlert: (uid) => request(`/alerts/${uid}/ack`, { method: "POST" }),
  calibration: () => request("/calibration"),
  predictions: (status, limit = 60) =>
    request(`/predictions?${status ? `status=${status}&` : ""}limit=${limit}`),
  broadcasts: (limit = 50) => request(`/broadcasts?limit=${limit}`),
  foresight: () => request("/foresight"),
  foresightSkill: () => request("/foresight/skill"),
  broadcastStatus: () => request("/broadcast/status"),
  broadcastAlert: (uid) => request(`/alerts/${uid}/broadcast`, { method: "POST", timeout: 30000, tries: 1 }),
};
